import React from "react";
import * as Sentry from '@sentry/react';
import { VERSION } from '../version.js';

// Сбой подгрузки lazy-чанка: вкладка пережила деплой, либо обрыв/смена сети на стороне
// клиента (crash-report 03.09: запрос за чанком до nginx не дошёл, файл на сервере был).
// Перезагрузка это чинит, а экран краша и CRASH-алерт только пугают. Тексты трёх
// движков: Chrome / Firefox / Safari.
const CHUNK_LOAD_RE = /Failed to fetch dynamically imported module|error loading dynamically imported module|Importing a module script failed/i;
export const isChunkLoadError = (err) => CHUNK_LOAD_RE.test(String(err?.message || err || ''));

// ── Error Boundary — prevents white screen crashes ──
// v31.41: теперь отправляет crash report на backend (POST /api/crash-report) →
// JSONL в audit/ + Telegram alert админам. Плюс auto-reload через 5 сек для
// transient crashes (например deploy mid-render race). Loop-guard: если crash
// случается 2-й раз подряд в пределах 60 сек — auto-reload отключается, user
// видит stable crash screen и может вручную отправить / откатить.
//
// C1 (2026-05-29): extracted VERBATIM from src/App.jsx to src/components/CrashGuard.jsx.
// Behavior-preserving — class body byte-identical (до 2026-09-03: тихая перезагрузка
// при сбое подгрузки lazy-чанка + seam reloadPage(), см. isChunkLoadError ниже). Deps: React (Component +
// createElement), Sentry (@sentry/react captureException), VERSION (../version.js).
// Takes `children`; the root App still renders <CrashGuard><AppInner/></CrashGuard>.
export class CrashGuard extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null, errorInfo: null, countdown: null, reported: false, loopDetected: false, chunkReload: false };
    this.countdownTimer = null;
  }
  static getDerivedStateFromError(error) { return { hasError: true, error }; }
  componentDidCatch(error, errorInfo) {
    this.setState({ errorInfo });
    console.error("🔴 CrashGuard caught:", error, errorInfo);

    const LOOP_KEY = 'blitz_crash_at';
    const CHUNK_RELOAD_KEY = 'blitz_chunk_reload_at';
    const AUTO_RELOAD_SEC = 5;
    const LOOP_WINDOW_MS = 60000;
    const now = Date.now();

    // 2026-09-03: сбой подгрузки чанка → одна тихая перезагрузка сразу: без Sentry, без
    // /api/crash-report, без экрана краша. Повтор в пределах 60 с = перезагрузка не
    // помогла → обычный путь ниже как crash loop (отчёт + стабильный экран, без второго
    // авто-reload).
    let chunkReloadFailed = false;
    if (isChunkLoadError(error)) {
      const lastChunkReload = parseInt(sessionStorage.getItem(CHUNK_RELOAD_KEY) || '0', 10);
      if (!lastChunkReload || now - lastChunkReload >= LOOP_WINDOW_MS) {
        sessionStorage.setItem(CHUNK_RELOAD_KEY, String(now));
        console.warn('♻️ CrashGuard: chunk load failed, reloading once:', String(error?.message || error));
        this.setState({ chunkReload: true });
        this.reloadPage();
        return;
      }
      chunkReloadFailed = true;
    }

    // 2026-05-20 (ADR-012): mirror к Sentry в addition к backend /api/crash-report.
    // Sentry auto-groups errors by stack signature + adds release/env context for
    // dashboard archaeology. Existing backend path (POST /api/crash-report → audit
    // log + Telegram alert) preserved для defense-in-depth — Sentry needs network
    // к its ingest endpoint, our /api/crash-report works on local network.
    try {
      Sentry.captureException(error, {
        contexts: {
          react: { componentStack: errorInfo?.componentStack || '' },
        },
      });
    } catch (_) { /* never block crash UI on telemetry */ }

    // Loop detection: если последний crash был <60 сек назад, не auto-reload'ить.
    // sessionStorage сохраняется через reload, но очищается при закрытии tab.
    const lastCrashAt = parseInt(sessionStorage.getItem(LOOP_KEY) || '0', 10);
    const isLoop = chunkReloadFailed || (lastCrashAt && (now - lastCrashAt < LOOP_WINDOW_MS));
    sessionStorage.setItem(LOOP_KEY, String(now));

    // Report to backend (fire & forget — не ждём ответа, не блокируем UI).
    // Body validated + truncated серверной стороной.
    try {
      // Try to get current user email. В blitz-crm user state живёт в memory
      // (AppInner), но при login saved в blitz_session для restore после reload.
      let email = '';
      try {
        const s = JSON.parse(localStorage.getItem('blitz_session') || '{}');
        email = s?.user?.email || s?.email || '';
      } catch {}
      const page = (typeof window !== 'undefined' && window.location?.pathname) || '';
      const stack = (error?.stack || '').slice(0, 3000);
      const componentStack = (errorInfo?.componentStack || '').slice(0, 2000);
      fetch('/api/crash-report', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          version: VERSION,
          email,
          url: window.location.href.slice(0, 300),
          error: String(error?.message || error).slice(0, 500),
          stack,
          componentStack,
          userAgent: (navigator.userAgent || '').slice(0, 200),
          page,
        }),
      }).then(() => this.setState({ reported: true })).catch(() => {});
    } catch {}

    if (isLoop) {
      this.setState({ loopDetected: true });
      return; // no auto-reload — показываем stable crash screen
    }

    // Auto-reload countdown
    this.setState({ countdown: AUTO_RELOAD_SEC });
    this.countdownTimer = setInterval(() => {
      this.setState(s => {
        if (s.countdown === null) return s;
        if (s.countdown <= 1) {
          clearInterval(this.countdownTimer);
          this.reloadPage();
          return { countdown: 0 };
        }
        return { countdown: s.countdown - 1 };
      });
    }, 1000);
  }
  cancelAutoReload = () => {
    if (this.countdownTimer) clearInterval(this.countdownTimer);
    this.countdownTimer = null;
    this.setState({ countdown: null });
  };
  // Единственная точка перезагрузки: window.location в jsdom не подменить,
  // тесты шпионят за этим методом.
  reloadPage() { window.location.reload(); }
  componentWillUnmount() {
    if (this.countdownTimer) clearInterval(this.countdownTimer);
  }
  render() {
    if (this.state.hasError) {
      if (this.state.chunkReload) {
        return React.createElement("div", { style: { minHeight: "100vh", display: "flex", alignItems: "center", justifyContent: "center", background: "#0F172A", fontFamily: "monospace", color: "#94A3B8", fontSize: 14 } },
          "♻️ Updating Blitz CRM…"
        );
      }
      const err = this.state.error;
      const info = this.state.errorInfo;
      const stack = err?.stack || "";
      const componentStack = info?.componentStack || "";
      const { countdown, reported, loopDetected } = this.state;
      return React.createElement("div", { style: { minHeight: "100vh", display: "flex", alignItems: "center", justifyContent: "center", background: "#0F172A", fontFamily: "monospace", color: "#E2E8F0", padding: 20 } },
        React.createElement("div", { style: { maxWidth: 700, width: "100%" } },
          React.createElement("h1", { style: { color: "#EF4444", fontSize: 24, marginBottom: 8 } }, "⚠️ Blitz CRM Crash Report"),
          React.createElement("p", { style: { color: "#94A3B8", fontSize: 14, marginBottom: 12 } }, `v${VERSION} — ${reported ? '✓ Report sent to admins' : 'Sending report...'}`),
          // Auto-reload countdown или loop warning
          countdown !== null && countdown > 0 && React.createElement("div", { style: { background: "#1E40AF20", border: "1px solid #3B82F6", borderRadius: 8, padding: 12, marginBottom: 16, fontSize: 13, color: "#93C5FD" } },
            `🔄 Auto-reloading in ${countdown}s... (click "Cancel reload" if you want to copy error)`
          ),
          loopDetected && React.createElement("div", { style: { background: "#7F1D1D40", border: "1px solid #EF4444", borderRadius: 8, padding: 12, marginBottom: 16, fontSize: 13, color: "#FCA5A5" } },
            `⛔ Crash loop detected (повторился в течение 60 сек). Auto-reload отключён. Пришли админу stack ниже.`
          ),
          React.createElement("div", { style: { background: "#1E293B", border: "1px solid #334155", borderRadius: 8, padding: 16, marginBottom: 16, fontSize: 13, lineHeight: 1.6, whiteSpace: "pre-wrap", wordBreak: "break-word", maxHeight: 300, overflow: "auto" } },
            "Error: " + String(err?.message || err) + "\n\n" +
            "Stack:\n" + stack.split("\n").slice(0, 8).join("\n") + "\n\n" +
            "Component:\n" + componentStack.split("\n").slice(0, 10).join("\n")
          ),
          React.createElement("div", { style: { display: "flex", gap: 12, flexWrap: "wrap" } },
            React.createElement("button", {
              onClick: () => { try { navigator.clipboard.writeText("Error: " + String(err?.message || err) + "\nStack: " + stack + "\nComponent: " + componentStack); } catch(e) {} },
              style: { padding: "10px 24px", borderRadius: 8, background: "#6366F1", border: "none", color: "#FFF", cursor: "pointer", fontSize: 14, fontWeight: 600 }
            }, "📋 Copy Error"),
            countdown !== null && countdown > 0 && React.createElement("button", {
              onClick: this.cancelAutoReload,
              style: { padding: "10px 24px", borderRadius: 8, background: "#F59E0B", border: "none", color: "#FFF", cursor: "pointer", fontSize: 14, fontWeight: 600 }
            }, "⏸ Cancel reload"),
            React.createElement("button", {
              onClick: () => { this.cancelAutoReload(); this.setState({ hasError: false, error: null, errorInfo: null, reported: false, loopDetected: false }); },
              style: { padding: "10px 24px", borderRadius: 8, background: "#0EA5E9", border: "none", color: "#FFF", cursor: "pointer", fontSize: 14, fontWeight: 600 }
            }, "🔄 Try Again"),
            React.createElement("button", {
              onClick: () => { this.cancelAutoReload(); this.reloadPage(); },
              style: { padding: "10px 24px", borderRadius: 8, background: "transparent", border: "1px solid #475569", color: "#94A3B8", cursor: "pointer", fontSize: 14 }
            }, "Reload Page")
          )
        )
      );
    }
    return this.props.children;
  }
}

export default CrashGuard;
